// Figma: no frame provided for this section yet — built using the established design
// system (DataTableShell, tokens) per the "extrapolate" decision.
// LOGIC PORTED from LC2's DataAlerts.js (layout/MainLayout/Header/HeaderContent/):
// same useGetAccountAlertsQuery + useLazyGetAccountAlertsQuery (date-range refetch), same
// isStakeAdmin/isAdministrator/propertyPermissions filtering, same campaign_sfid/
// community_sfid scoping. Simplified: the "Last 7/30/60 days" shortcut buttons are ported;
// the custom date-range picker (@mui/x-date-pickers) is dropped for this pass to avoid
// pulling in that dependency — flagged below.
import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {Box, Button, Stack, Typography} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';

import {useGetAccountAlertsQuery, useLazyGetAccountAlertsQuery} from '../../features/coreApi/apiSlice';
import {getIsStakeAdmin, getPropertyPermissions, getTeamMemberRole} from '../../app/store/reducers/session';
import DataTableShell from './components/DataTableShell';

const SHORTCUTS = [
    {label: 'Last 7 days', days: 7},
    {label: 'Last 30 Days', days: 30},
    {label: 'Last 60 Days', days: 60}
];

const AlertsSection = () => {
    const params = useParams();
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const teamMemberRole = useSelector(getTeamMemberRole);
    const isAdministrator = teamMemberRole === 'Administrator';
    const propertyPermissions = useSelector(getPropertyPermissions);

    const [selectedRange, setSelectedRange] = useState('Last 30 Days');
    // Tracks which account the custom date-range fetch below was made for, so a shortcut
    // clicked before switching accounts doesn't keep shadowing the (correctly refetching)
    // default 30-day query with a different account's stale data.
    const [customRangeAccount, setCustomRangeAccount] = useState(null);

    const {data: alerts, isLoading, isFetching, isSuccess, isError} = useGetAccountAlertsQuery(
        {sfid: params?.account_sfid},
        {skip: !params?.account_sfid}
    );
    const [fetchAlerts, {data: customAlerts, isSuccess: isCustomSuccess, isLoading: isCustomLoading, isFetching: isCustomFetching}] =
        useLazyGetAccountAlertsQuery();

    useEffect(() => {
        setSelectedRange('Last 30 Days');
        setCustomRangeAccount(null);
    }, [params?.account_sfid]);

    const alertsData = useMemo(() => {
        const useCustomRange = isCustomSuccess && customAlerts && customRangeAccount === params?.account_sfid;
        const displayData = useCustomRange ? customAlerts : isSuccess && alerts ? alerts : [];
        if (!displayData?.length) return [];
        const filtered =
            isStakeAdmin || isAdministrator ? displayData : displayData.filter((alert) => propertyPermissions.includes(alert.property_sfid));

        if (params?.campaign_sfid) return filtered.filter((alert) => alert.campaign_sfid === params.campaign_sfid);
        if (params?.community_sfid) return filtered.filter((alert) => alert.property_sfid === params.community_sfid);
        return filtered;
    }, [isCustomSuccess, customAlerts, customRangeAccount, isSuccess, alerts, params, isStakeAdmin, isAdministrator, propertyPermissions]);

    const handleShortcut = (shortcut) => {
        setSelectedRange(shortcut.label);
        const today = dayjs();
        const startDate = today.subtract(shortcut.days, 'day').format('YYYY-MM-DD');
        const endDate = today.format('YYYY-MM-DD');
        setCustomRangeAccount(params?.account_sfid);
        fetchAlerts({sfid: params?.account_sfid, startDate, endDate});
    };

    const columns = useMemo(
        () => [
            {field: 'property_name', headerName: 'Property', width: 180},
            {field: 'contact_name', headerName: 'Resident', width: 160},
            {field: 'alert_description', headerName: 'Description', width: 320},
            {field: 'effective_at', headerName: 'Last Updated', width: 140},
            {
                field: 'is_resolved',
                headerName: 'Is Resolved?',
                width: 110,
                renderCell: (row) =>
                    row.is_resolved ? <DoneIcon color="success" fontSize="small" /> : <CloseIcon color="error" fontSize="small" />
            }
        ],
        []
    );

    const isBusy = isLoading || isFetching || isCustomLoading || isCustomFetching;

    return (
        <Box sx={{border: '1px solid', borderColor: 'divider', borderRadius: '6px', backgroundColor: 'background.paper', p: 2}}>
            <Stack spacing={0.5} sx={{mb: 2}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Alerts
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Monitor and track errors with your resident data that Stake receives from your PMS to ensure that your Stake programs
                    are running smoothly.
                </Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{mb: 2}}>
                {SHORTCUTS.map((shortcut) => (
                    <Button
                        key={shortcut.label}
                        size="small"
                        variant={selectedRange === shortcut.label ? 'contained' : 'outlined'}
                        onClick={() => handleShortcut(shortcut)}
                    >
                        {shortcut.label}
                    </Button>
                ))}
                {/* TODO: custom date-range picker dropped for this pass (needs @mui/x-date-pickers) */}
            </Stack>
            {isBusy ? (
                <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 4}}>
                    Loading alerts…
                </Typography>
            ) : isError ? (
                <Typography variant="body2" color="error.main" sx={{textAlign: 'center', py: 4}}>
                    Failed to fetch alerts
                </Typography>
            ) : (
                <DataTableShell
                    title=""
                    description=""
                    columns={columns}
                    rows={alertsData}
                    getRowId={(row) => row.id}
                    searchPlaceholder="Search alerts"
                    filterable
                />
            )}
        </Box>
    );
};

export default AlertsSection;
